import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import TermsDocument from './TermsDocument';

// Dismissal lasts for the browser session only. Remembering it forever would turn the
// re-prompt into a one-shot notice; forgetting it per page view would make the portal
// unusable. The hard stop lives on the server, on payout requests alone.
const DISMISS_KEY = 'expedify_terms_prompt_dismissed';

/**
 * Mounted inside the authed layout. Asks a partner whose recorded acceptance is for an older
 * version to accept the current one.
 *
 * Deliberately a soft prompt: it can be dismissed, and nothing in the portal stops working.
 * The only hard gate is the payout endpoint, which returns 403 until this is done. Locking a
 * partner out of the whole portal over a terms revision costs more than it protects.
 */
const TermsGate = () => {
  const { user, token, getAuthHeaders, API_URL } = useAuth();
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Admins are internal and are not party to the partner agreement.
    if (!token || user?.role !== 'partner') return;
    let cancelled = false;
    axios
      .get(`${API_URL}/api/auth/terms-status`, { headers: getAuthHeaders() })
      .then(({ data }) => {
        if (cancelled) return;
        setStatus(data);
        const dismissed = sessionStorage.getItem(DISMISS_KEY) === data.current_version;
        if (data.needs_acceptance && !dismissed) setOpen(true);
      })
      .catch(() => {}); // a failed poll must never block the page
    return () => { cancelled = true; };
  }, [token, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismiss = () => {
    if (status) sessionStorage.setItem(DISMISS_KEY, status.current_version);
    setOpen(false);
  };

  const accept = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/auth/accept-terms`, {}, { headers: getAuthHeaders() });
      sessionStorage.removeItem(DISMISS_KEY);
      setOpen(false);
      toast.success('Thanks — your acceptance has been recorded.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not record your acceptance. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!status?.needs_acceptance) return null;

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? setOpen(true) : dismiss())}>
      <DialogContent className="max-w-2xl" data-testid="terms-gate">
        <DialogHeader>
          <DialogTitle>The partner terms have been updated</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-[#64748B]">
          {status.accepted_version
            ? `You accepted version ${status.accepted_version}. Version ${status.current_version} is now current.`
            : `Please review and accept version ${status.current_version} of the partner terms.`}
          {' '}Payout requests are paused until you accept.
        </p>

        <div className="max-h-[45vh] overflow-y-auto rounded-lg border border-[#E2E2DF] bg-white p-5">
          <TermsDocument />
        </div>

        <div className="flex items-start gap-3 p-4 bg-[#FAFAF8] rounded-lg border border-[#E2E2DF]">
          <Checkbox
            id="accept-updated-terms"
            checked={agreed}
            onCheckedChange={setAgreed}
            className="mt-0.5"
            data-testid="terms-gate-checkbox"
          />
          <Label htmlFor="accept-updated-terms" className="text-sm text-[#64748B] cursor-pointer leading-relaxed">
            I have read and agree to version {status.current_version} of the Expedify partner terms.
            {' '}
            <Link to="/terms" target="_blank" rel="noreferrer" className="text-[#ee785b] hover:underline">
              Open in a new tab
            </Link>
          </Label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={dismiss} className="border-[#E2E2DF]">
            Later
          </Button>
          <Button
            onClick={accept}
            disabled={!agreed || saving}
            className="bg-[#ee785b] hover:bg-[#d66547] text-white disabled:opacity-50"
            data-testid="terms-gate-accept"
          >
            {saving ? 'Recording…' : 'Accept terms'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TermsGate;
